import RateChangeLogService from "@/services/RateChangeLogService";

export const state = {
  list_status: 0,
  list: [],
  list_total: 0,
  detail_status: 0,
  detail: null,
  filters: {
    from: null,
    to: null,
    stay_from: null,
    stay_to: null,
    source: null,
    origin: null,
    user_id: null,
    category: null,
    pms_sync_status: null,
    with_incidents: null,
    search: null,
    sort_by: null,
    sort_desc: null,
    page: 1,
    per_page: 25
  }
};

export const mutations = {
  SET_LIST_STATUS(state, status) {
    state.list_status = status;
  },
  SET_LIST(state, { items, total }) {
    state.list = items || [];
    state.list_total = total || 0;
  },
  SET_DETAIL_STATUS(state, status) {
    state.detail_status = status;
  },
  SET_DETAIL(state, detail) {
    state.detail = detail;
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  UPDATE_LIST_ITEM(state, item) {
    const index = state.list.findIndex(i => i.id === item.id);
    if (index >= 0) {
      state.list.splice(index, 1, { ...state.list[index], ...item });
    }
  },
  MERGE_STATUSES(state, items) {
    if (!Array.isArray(items)) return;
    items.forEach(status => {
      const index = state.list.findIndex(i => i.id === status.id);
      if (index >= 0) {
        state.list.splice(index, 1, { ...state.list[index], ...status });
      }
    });
  }
};

function buildParams(rootGetters, filters) {
  const params = { hotel_id: rootGetters.current_hotel.id };
  const f = filters;
  if (f.from) params.from = f.from;
  if (f.to) params.to = f.to;
  if (f.stay_from) params.stay_from = f.stay_from;
  if (f.stay_to) params.stay_to = f.stay_to;
  if (f.source) params.source = f.source;
  if (f.origin) params.origin = f.origin;
  if (f.user_id) params.user_id = f.user_id;
  if (f.category) params.category = f.category;
  if (f.pms_sync_status) params.pms_sync_status = f.pms_sync_status;
  if (f.with_incidents) params.with_incidents = 1;
  if (f.search) params.search = f.search;
  if (f.sort_by) {
    params.sort_by = f.sort_by;
    params.sort_desc = f.sort_desc ? 1 : 0;
  }
  const page = f.page || 1;
  const perPage = f.per_page || 25;
  params.limit = perPage;
  params.offset = (page - 1) * perPage;
  return params;
}

export const actions = {
  setRateChangeLogFilters({ commit }, filters) {
    commit("SET_FILTERS", filters);
  },
  fetchRateChangeLogList({ commit, rootGetters, state }) {
    commit("SET_LIST_STATUS", 1);

    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }

    const params = buildParams(rootGetters, state.filters);

    return RateChangeLogService.list(params)
      .then(response => {
        const data = response.data;
        commit("SET_LIST", {
          items: data.items || data.data || [],
          total: data.total || 0
        });
        commit("SET_LIST_STATUS", 2);
        return data;
      })
      .catch(() => {
        commit("SET_LIST_STATUS", 0);
        return Promise.reject();
      });
  },
  // Lightweight poll: only refresh sync/snapshot status of the rows still pending,
  // instead of re-fetching the whole list.
  fetchRateChangeLogStatuses({ commit, rootGetters, getters }) {
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }
    const ids = getters.rateChangeLogPendingIds;
    if (!ids.length) return Promise.resolve();

    return RateChangeLogService.statuses({
      hotel_id: rootGetters.current_hotel.id,
      ids: ids.join(",")
    }).then(response => {
      commit("MERGE_STATUSES", (response.data && response.data.items) || []);
      return response.data;
    });
  },
  fetchRateChangeLogDetail({ commit, rootGetters }, id) {
    commit("SET_DETAIL_STATUS", 1);

    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }

    return RateChangeLogService.detail(id, {
      hotel_id: rootGetters.current_hotel.id
    })
      .then(response => {
        const data = response.data;
        commit("SET_DETAIL", data);
        commit("SET_DETAIL_STATUS", 2);
        return data;
      })
      .catch(() => {
        commit("SET_DETAIL_STATUS", 0);
        return Promise.reject();
      });
  },
  // Fetches a single change's detail without mutating the shared detail state.
  // Used to build the Excel export including cascade rows.
  fetchRateChangeLogDetailById({ rootGetters }, id) {
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return Promise.resolve(null);
    }
    return RateChangeLogService.detail(id, {
      hotel_id: rootGetters.current_hotel.id
    })
      .then(response => response.data)
      .catch(() => null);
  },
  retryRateChangeLogPms({ commit, rootGetters, dispatch }, id) {
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }

    return RateChangeLogService.retryPms(id, {
      hotel_id: rootGetters.current_hotel.id
    }).then(response => {
      const data = response.data;
      if (data && data.id) {
        commit("UPDATE_LIST_ITEM", data);
      } else {
        dispatch("fetchRateChangeLogDetail", id);
      }
      return data;
    });
  },
  retryRateChangeLogSnapshot({ rootGetters }, id) {
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }
    return RateChangeLogService.retrySnapshot(id, {
      hotel_id: rootGetters.current_hotel.id
    }).then(response => response.data);
  },
  clearRateChangeLogDetail({ commit }) {
    commit("SET_DETAIL", null);
    commit("SET_DETAIL_STATUS", 0);
  }
};

export const getters = {
  rateChangeLogPendingIds: state =>
    state.list
      .filter(
        item =>
          item.pms_sync_status === "pending" ||
          item.snapshot_status === "pending"
      )
      .map(item => item.id),
  rateChangeLogHasPending: (state, getters) =>
    getters.rateChangeLogPendingIds.length > 0
};
